import { useEffect } from "react";
import { AppShell } from "./app-shell";
import { LoginPage } from "../modules/session/login-page";
import { LoadingPage } from "../modules/session/loading-page";
import { loadCurrentMember, loginWithGoogleAndLoadMember } from "../modules/session/session-service";
import { observeCurrentUser } from "../shared/firebase/firebase-auth";
import { useAppStore } from "./app-store";

function errorMessage(error: unknown): string {
  if (error && typeof error === "object" && "code" in error) {
    return String(error.code);
  }
  return error instanceof Error ? error.message : "로그인할 수 없습니다.";
}

export function AppRoot() {
  const authStatus = useAppStore((state) => state.authStatus);
  const loginStatus = useAppStore((state) => state.loginStatus);
  const authError = useAppStore((state) => state.authError);
  const beginAuthCheck = useAppStore((state) => state.beginAuthCheck);
  const setLoginBusy = useAppStore((state) => state.setLoginBusy);
  const setSignedOut = useAppStore((state) => state.setSignedOut);
  const setSignedIn = useAppStore((state) => state.setSignedIn);
  const syncPlatform = useAppStore((state) => state.syncPlatform);

  useEffect(() => {
    syncPlatform();
    window.addEventListener("resize", syncPlatform);
    return () => window.removeEventListener("resize", syncPlatform);
  }, [syncPlatform]);

  useEffect(() => {
    let authCheckId = 0;
    const unsubscribe = observeCurrentUser((user) => {
      const currentCheckId = authCheckId + 1;
      authCheckId = currentCheckId;

      if (!user) {
        setSignedOut();
        return;
      }

      beginAuthCheck();
      void loadCurrentMember()
        .then((member) => {
          if (authCheckId === currentCheckId) {
            setSignedIn(member);
          }
        })
        .catch((error) => {
          if (authCheckId === currentCheckId) {
            setSignedOut(errorMessage(error));
          }
        });
    });

    return () => {
      authCheckId += 1;
      unsubscribe();
    };
  }, [beginAuthCheck, setSignedIn, setSignedOut]);

  async function handleLogin(): Promise<void> {
    setLoginBusy(true);
    try {
      setSignedIn(await loginWithGoogleAndLoadMember());
    } catch (error) {
      setSignedOut(errorMessage(error));
    }
  }

  if (authStatus === "checking") {
    return <LoadingPage />;
  }

  if (authStatus === "signed-out") {
    return <LoginPage busy={loginStatus === "busy"} error={authError} onLogin={handleLogin} />;
  }

  return (
    <AppShell>
      <div className="app-empty-surface" />
    </AppShell>
  );
}
